import { SelectedTransactionsData } from './bot/types';
import { BetHistoryEntity, BetHistoryEntitySelection, TransStatement } from './api/model';

/**
 * Tag generation configuration for different transaction types
 */
type TagConfig<T = any> = {
  prefix: string;
  type: string;
  idField: keyof T;
};

const TAG_CONFIG = {
  bets: { prefix: 'u_b_', type: 'b', idField: 'orderId' as keyof BetHistoryEntity },
  userEvents: { prefix: 'u_bvt_', type: 'bvt', idField: 'id' as keyof BetHistoryEntitySelection },
  withdrawals: { prefix: 'u_w_', type: 'w', idField: 'tradeId' as keyof TransStatement },
  deposits: { prefix: 'u_d_', type: 'd', idField: 'tradeId' as keyof TransStatement },
} as const;

/**
 * Generates tags from selectedTransactionsData object
 * @param selectedTransactionsData - Object containing betsSelected, userEventsSelections, withdrawals, and deposits arrays
 * @returns Array of string tags
 */
export const generateSelectedTransactionsTags = (selectedTransactionsData: SelectedTransactionsData): string[] => {
  const { betsSelected, userEventsSelections, withdrawals, deposits } = selectedTransactionsData;

  const tags: string[] = [];

  // Generate tags for each transaction type
  tags.push(...generateTagsForArray<BetHistoryEntity>(betsSelected, TAG_CONFIG.bets));
  tags.push(...generateTagsForArray<BetHistoryEntitySelection>(userEventsSelections, TAG_CONFIG.userEvents));
  tags.push(...generateTagsForArray<TransStatement>(withdrawals, TAG_CONFIG.withdrawals));
  tags.push(...generateTagsForArray<TransStatement>(deposits, TAG_CONFIG.deposits));

  // Remove duplicate tags
  const uniqueTags = Array.from(new Set(tags));
  return uniqueTags;
};

/**
 * Generates tags for a specific array of transactions
 * @param items - Array of transaction items
 * @param config - Tag configuration object
 * @returns Array of generated tags
 */
const generateTagsForArray = <T>(items: T[] | undefined, config: TagConfig<T>): string[] => {
  if (!items?.length) {
    return [];
  }

  return items
    .map((item) => {
      const id = item[config.idField];
      if (!id) return null;

      const tag = `${config.prefix}${id}`;
      return validateAndProcessTag(tag, config.type);
    })
    .filter((tags): tags is string[] => !!tags?.length)
    .flat();
};

/**
 * Validates tag length and processes it according to requirements
 * @param tag - The tag string to validate
 * @param type - The type of transaction (b, bvt, w, d)
 * @returns Array of processed tags
 */
const validateAndProcessTag = (tag: string, type: string): string[] => {
  if (tag.length <= 30) return [tag];

  // Remove last character, add ~, and add incomplete tag
  const truncatedTag = tag.slice(0, 29) + '~';
  return [truncatedTag, `u_${type}_id_incomplete`];
};

/**
 * Checks if selectedTransactionsData has any selections
 * @param selectedTransactionsData - Object containing betsSelected, userEventsSelections, withdrawals, and deposits arrays
 * @returns boolean indicating if any selections exist
 */
export const hasSelectedTransactions = (selectedTransactionsData: SelectedTransactionsData): boolean => {
  const { betsSelected, userEventsSelections, withdrawals, deposits } = selectedTransactionsData;

  return [betsSelected, userEventsSelections, withdrawals, deposits]
    .some(array => !!array?.length);
};

/**
 * Merges new selectedTransactionsData with existing data, preserving previous selections
 * @param existingData - Existing selectedTransactionsData (can be undefined)
 * @param newData - New selectedTransactionsData to merge
 * @returns Merged selectedTransactionsData
 */
export const mergeSelectedTransactionsData = (
  existingData: SelectedTransactionsData | undefined,
  newData: SelectedTransactionsData
): SelectedTransactionsData => {
  if (!existingData) {
    return newData;
  }

  return {
    betsSelected: [...(existingData.betsSelected || []), ...(newData.betsSelected || [])],
    userEventsSelections: [...(existingData.userEventsSelections || []), ...(newData.userEventsSelections || [])],
    withdrawals: [...(existingData.withdrawals || []), ...(newData.withdrawals || [])],
    deposits: [...(existingData.deposits || []), ...(newData.deposits || [])],
  };
};

/**
 * localStorage key for storing selectedTransactionsData
 */
export const SELECTED_TRANSACTIONS_STORAGE_KEY = 'cache:selectedTransactionsData';

/**
 * Saves selectedTransactionsData to localStorage
 * @param selectedTransactionsData - The data to save
 */
export const saveSelectedTransactionsToStorage = (selectedTransactionsData: SelectedTransactionsData): void => {
  localStorage.setItem(SELECTED_TRANSACTIONS_STORAGE_KEY, JSON.stringify(selectedTransactionsData));
};

/**
 * Loads selectedTransactionsData from localStorage
 * @returns The stored data or null if not found/invalid
 */
export const loadSelectedTransactionsFromStorage = (): SelectedTransactionsData | null => {
  try {
    const stored = localStorage.getItem(SELECTED_TRANSACTIONS_STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const parsed = JSON.parse(stored) as SelectedTransactionsData;
    return parsed;
  } catch (error) {
    return null;
  }
};

/**
 * Clears selectedTransactionsData from localStorage
 */
export const clearSelectedTransactionsFromStorage = (): void => {
  localStorage.removeItem(SELECTED_TRANSACTIONS_STORAGE_KEY);
};
